import styles from './styles.module.scss';
import { Resource, UpgradeType } from '../../types/game-types';
import { useUpgradesStore } from '../../store/zustand/useUpgradesStore';
import { humanNumber } from '@game-libs/exc-libs';
import { useTranslation } from 'react-i18next';
import { useItemCost } from '../../store/hooks/useItemCost';
import { Icon } from '../../gui/Icon';
import { useGeneratorContext } from '../../store/generator-context';

export const PurchaseUpgrade = ({ id }: { id: string }) => {
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const entityType = useGeneratorContext();
  const upgrade = upgrades[entityType][id as UpgradeType];

  const { t } = useTranslation('upgrades', { keyPrefix: 'details.' + upgrade.id });
  const { t: tUpgrades } = useTranslation('upgrades');
  const { t: tGen } = useTranslation('generators');

  const [cost, , , currenciesWithoutFunds] = useItemCost(upgrade, false);

  const isMaxed = upgrade.purchased >= (upgrade.purchaseLimit || Infinity);

  const isEventTypeUpgrade = upgrade.multiplier === 0 && upgrade.bonusPerOwned === 0;

  if (upgrade.isLockedOnDemo) {
    return <div className={styles.container}>{tUpgrades('lockedOnDemo')}</div>;
  }

  return (
    <div className={`${styles.container}`}>
      <div className={styles.title}>
        <span>{t('title')}</span>
        {upgrade.purchaseLimit && (
          <span className={styles.count}>
            {upgrade.purchased} / {upgrade.purchaseLimit || '∞'}
          </span>
        )}
      </div>
      <div className={styles.description}>
        {t('description', {
          type: tGen(entityType, { count: 1 }),
          typePlural: tGen(entityType, { count: 2 }),
          bonus: upgrade.bonusPerOwned,
          current: humanNumber(upgrade.multiplier, 3),
        })}
      </div>
      <div className={`${styles.valueTransition} ${isEventTypeUpgrade ? styles.isEventTypeUpgrade : ''}`}>
        {!isEventTypeUpgrade && (
          <>
            <span className={styles.before}>
              {t('prefix', { defaultValue: '' })}
              {humanNumber(upgrade.multiplier, 3)}
              {t('suffix', { defaultValue: '' })}
            </span>
            {!isMaxed && (
              <>
                <span className={styles.arrow}>{'>>>'}</span>
                <span className={styles.after}>
                  {t('prefix', { defaultValue: '' })}
                  {humanNumber(upgrade.multiplier + upgrade.bonusPerOwned, 3)}
                  {t('suffix', { defaultValue: '' })}
                </span>
              </>
            )}
          </>
        )}
      </div>
      <div className={styles.costContainer}>
        {isMaxed ? (
          <div>{tUpgrades('max')}</div>
        ) : (
          Object.keys(upgrade.cost).map((currency) => {
            return (
              <div
                key={upgrade.id}
                className={`${styles.cost} ${currenciesWithoutFunds.includes(currency as Resource) ? styles.notEnoughFunds : ''}`}
              >
                <Icon iconId={`item_cost_${currency}`} />
                <span>{humanNumber(cost[currency as Resource]!)}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
